import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { stringify } from 'querystring';
import {
  any,
  bool,
  func,
  shape,
  string,
} from 'prop-types';
import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  SvgIcon,
  Typography,
} from '@material-ui/core';
import {
  CloudDownload as CloudDownloadIcon,
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  FindInPage as FindInPageIcon,
  DeleteOutline as DeleteOutlineIcon,
  AssignmentTurnedIn,
} from '@material-ui/icons';

import { Loader, Modal } from 'components';
import {
  download,
  getSignedUrl,
  upload,
} from 'utils/aws/s3';
import { parseIfValid } from 'utils/json';

import { DOCUMENT_VIEWER } from './constants';
import useStyles from './styles';

function DocumentControl(props) {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState();
  const [pendingFiles, setPendingFiles] = useState([]);
  const [state, setState] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const fileId = `frm_input_file_${props?.name}`;
  const { t } = useTranslation('translation', {
    keyPrefix: 'components.forms.document_control',
  });

  const getType = function (ext) {
    if (['gif', 'jpg', 'jpeg', 'png'].includes(ext)) {
      return 'image';
    } else if (['doc', 'docx', 'pdf'].includes(ext)) {
      return 'document';
    } else {
      return 'other';
    }
  };

  const handleChangeFile = async function ({ target }) {
    const [file] = target.files;
    setIsLoading(true);
    setUploadError(false);

    if (file) {
      setPendingFiles([file]);
    }
  };

  const handleDeleteFile = function (index) {
    setState([]);
  };

  const handleDownload = async function (url) {
    await download(url, false);
    setModal();
  };

  const loadData = async function () {
    let initialValue;

    if (typeof props?.value === 'string') {
      initialValue = parseIfValid(props.value, false) || [];
    } else if (Array.isArray(props?.value)) {
      initialValue = props.value;
    } else {
      initialValue = [];
    }

    for await (const item of initialValue) {
      const ext = String(item?.url ?? '').toLowerCase().split('.').pop();

      if (item.active) {
        item.signedUrl = await getSignedUrl(item.url, 1000);
        item.type = getType(ext);
      }
    }

    setState(initialValue);
  };

  const uploadFile = async function () {
    const [file] = pendingFiles;
    const ext = ((file?.name ?? '').split('.').pop() || '').toLowerCase();
    const route = props?.settings?.route ?? 'tmp/';
    const metadata = {};
    let s3File = null;

    setIsLoading(true);
    setUploadError(false);

    for (let attempt = 1; attempt <= 5; attempt++) {
      try {
        s3File = await upload(
          `${route}${new Date().getTime()}.${String(Math.random()).substring(
            2,
            7,
          )}${ext ? `.${ext}` : ''}`,
          file,
          metadata,
        );

        const signedUrl = await getSignedUrl(s3File?.Location, 1000);

        setState([
          {
            active: true,
            metadata,
            name: props?.name,
            signedUrl,
            type: getType(ext),
            uploaded: new Date().getTime(),
            url: s3File?.Location,
          },
        ]);

        setPendingFiles([]);
        setUploadError(false);
        setIsLoading(false);
        return;
      } catch (error) {
        if (attempt === 5) {
          setUploadError(true);
          setIsLoading(false);
          setPendingFiles([]);
        }
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const target = {
      name: props?.name,
      value: state.map((item) => ({
        active: item.active,
        metadata: item.metadata ?? {},
        name: item.name,
        uploaded: item.uploaded,
        url: item.url,
      })),
    };

    props.onChange({
      target,
    });

    if (props?.events?.onChange) {
      props.events.onChange(target);
    }
  }, [state]);

  useEffect(() => {
    if (pendingFiles.length > 0 && !uploadError) {
      uploadFile();
    } else {
      setIsLoading(false);
    }
  }, [pendingFiles]);

  return (
    <>
      <input
        id={fileId}
        onChange={handleChangeFile}
        style={{
          display: 'none', 
        }}
        type="file"
      />

      <Modal
        actions={[
          {
            color: 'primary',
            fn: function () {
              handleDownload(modal?.data?.signedUrl);
            },
            hidden: !(modal?.view === 'preview'),
            icon: <CloudDownloadIcon />,
            label: 'Descargar',
          },
        ]}
        canFullScreen={modal?.canFullScreen}
        events={{
          onClose: setModal, 
        }}
        maxWidth={modal?.maxWidth}
        open={modal?.open}
        title={modal?.title}
      >
        {(modal?.view === 'preview') && (
          <>
            {modal?.data?.type === 'document' && (
              <iframe
                frameBorder="0"
                height="500px"
                src={`${DOCUMENT_VIEWER}?${stringify({
                  embedded: true,
                  url: encodeURIComponent(modal?.data?.signedUrl),
                })}`}
                title={t('preview')}
                width="100%"
              />
            )}
            {modal?.data?.type === 'image' && (
              <img
                alt=""
                src={modal?.data?.signedUrl}
                width="100%"
              />
            )}
            {modal?.data?.type === 'other' && (
              <Box textAlign="center">
                <Typography
                  children={modal?.data?.name}
                  gutterBottom
                  variant="h5"
                />
                <Box className={classes.file_unknown}>
                  <SvgIcon
                    children={<FindInPageIcon />}
                    fontSize="large"
                    size={60}
                  />
                </Box>
                <Typography
                  children={String(modal?.data?.url ?? '').split('/').pop()}
                  display="block"
                  gutterBottom
                  variant="caption"
                />
                <Typography
                  children="Vista previa no disponible"
                  gutterBottom
                  variant="body1"
                />
              </Box>
            )}
          </>
        )}
      </Modal>

      <Box className={classes.container}>
        <Grid container spacing={2}>
          {props?.label && (
            <Grid item xs={12}>
              <Typography
                color={props?.error ? 'error' : 'inherit'}
                children={props?.label}
                variant="body1"
                style={{
                  fontWeight: 700, 
                }}
              />
            </Grid>
          )}
          {props?.description && (
            <Grid item xs={12}>
              <Typography
                children={props?.description}
                variant="body2"
                style={{
                  marginTop: '-18px', 
                }}
              />
            </Grid>
          )}
          <Grid item md={5} sm={12} xs={12}>
            <Box className={classes.file_container}>
              {state.length === 0 ? (
                <>
                  {isLoading
                    ? <Loader />
                    : <CloudUploadOutlinedIcon className={classes.file_icon} />}

                  <Typography
                    align="center"
                    children={props?.placeholder}
                    variant="caption"
                  />

                  <Button
                    color="primary"
                    children={t('select_files')}
                    disabled={isLoading}
                    onClick={() => document.getElementById(fileId).click()}
                    className={classes.file_button}
                    size="small"
                    variant="contained"
                  />

                  {uploadError && (
                    <Box mt={2} textAlign="center">
                      <Typography color="error">
                        Falló la carga después de 5 intentos.
                      </Typography>
                      <Button
                        color="secondary"
                        onClick={() => {
                          setUploadError(false);
                          setPendingFiles(pendingFiles);
                        }}
                        variant="outlined"
                        size="small"
                      >
                        Reintentar
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <List>
                  {state.filter((item) => item?.active).map((item, index) => (
                    <ListItem key={index}>
                      {!hasError ? (
                        <div className={classes.preview}>
                          <img
                            className={classes.preview_image}
                            alt={`Imagen ${index}`}
                            src={item?.signedUrl}
                            onError={() => setHasError(true)}
                          />
                          <Button
                            children={t('delete_button')}
                            onClick={() => handleDeleteFile(item.index)}
                            size="small"
                            startIcon={<DeleteOutlineIcon />}
                            variant="outlined"
                            className={classes.preview_button}
                          />
                        </div>
                      ) : (
                        <div className={classes.preview}>
                          <AssignmentTurnedIn className={classes.upload_icon} />
                          <Button
                            children={t('delete_button')}
                            onClick={() => handleDeleteFile(item.index)}
                            size="small"
                            startIcon={<DeleteOutlineIcon />}
                            variant="outlined"
                            className={classes.preview_button}
                          />
                        </div>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

DocumentControl.defaultProps = {
  onChange: () => { },
  settings: {
    canDelete: true,
    readOnly: false,
  },
};

DocumentControl.propTypes = {
  error: string,
  events: shape({
    onChange: func,
  }),
  label: string,
  description: string,
  name: string,
  onChange: func,
  placeholder: string,
  settings: shape({
    canDelete: bool,
    readOnly: bool,
    route: string,
  }),
  value: any,
};

export default DocumentControl;
